// Outside service keys.
import { type CompanyLevel, KEY_PROVIDERS, keyMistake, type KeyProvider, keyProvidersAt } from "@steward/shared";
import { useState } from "react";
import { useKeys, useRemoveKey, useSaveKey } from "../../api/hooks";
import { useSession } from "../../app/session";
import { InputText } from "../../components/fields";
import { Icon } from "../../components/Icon";
import { Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, SettingRow, StatusLabel } from "../../components/parts";
import { errorText } from "./common";

// Each key's row name inside its company card and what it is for; the start of the key is named so a wrong paste shows
const PROVIDER_WORDS: Record<string, [string, string]> = {
  google_client_id: [
    "Sign-in client ID",
    "Lets each company press Sign in with Google on Marketing and pick its profile and Google Ads account. It ends in .apps.googleusercontent.com.",
  ],
  google_client_secret: ["Sign-in client secret", "The secret of the same sign-in client. It starts with GOCSPX-."],
  google: ["Server key", "Address lookup, routes and Google profile ratings. It starts with AIza."],
  google_maps: [
    "Maps key",
    "The maps on Home and the Schedule. It starts with AIza. Pages see it, so in Google lock it to our web addresses.",
  ],
  google_ads_token: [
    "Ads developer token (optional)",
    "Not needed now. Google gives Ads access through the Steward Cloud project. Only for an older token from Google Ads API Center.",
  ],
  google_service: ["Service account (backup)", "Only needed when nobody has signed in with Google. Paste the whole JSON file."],
  meta_app_id: [
    "App ID",
    "Lets each company press Sign in with Facebook on Marketing and pick its Page and ad account. In the app under App settings, Basic.",
  ],
  meta_app_secret: ["App secret", "On the same page as the App ID. Press Show to copy it."],
  meta: [
    "System user token (backup)",
    "Posts and ad spend for our own brands when nobody has signed in with Facebook. From Business Settings, System users.",
  ],
  quo: ["API key", "From Quo settings, API."],
  email: ["API key", "It starts with re_. Add and check each company's domain in Resend first."],
  anthropic: ["API key", "It starts with sk-ant-."],
  plaid_client_id: ["Client ID", "Only the owner types it in."],
  plaid_secret: ["Secret", "The secret of the same Plaid app."],
  fee_stripe: ["Secret key", "It starts with sk_ or rk_."],
  fee_stripe_pk: ["Publishable key", "It starts with pk_. The card fields a franchise owner saves the card with."],
  stripe: ["Secret key", "It starts with sk_ or rk_."],
  stripe_publishable: ["Publishable key", "It starts with pk_. Our pay page uses it for the card fields."],
  stripe_webhook: ["Webhook secret", "The signing secret of the webhook Stripe sends payments to. It starts with whsec_."],
};

// Keys grouped by the company that makes them, so each card says what that service runs (owner, 2026-11-02)
const KEY_GROUPS: { title: string; what: string; keys: string[] }[] = [
  {
    title: "Google",
    what: "Maps, address lookup, routes, your Google profile and reviews, Search Console and Google Ads.",
    keys: ["google_client_id", "google_client_secret", "google", "google_maps", "google_ads_token", "google_service"],
  },
  {
    title: "Meta",
    what: "Facebook and Instagram posts, Meta ad spend and leads.",
    keys: ["meta_app_id", "meta_app_secret", "meta"],
  },
  { title: "Quo", what: "Business texts and calls for every company.", keys: ["quo"] },
  { title: "Resend", what: "Invoices, receipts and invites by email, from each company's own address.", keys: ["email"] },
  { title: "Claude", what: "Eunice, the text receptionist, and Ask the Steward in Insights.", keys: ["anthropic"] },
  {
    title: "Plaid",
    what: "Links banks to Books, read only. Bank passwords never reach us.",
    keys: ["plaid_client_id", "plaid_secret"],
  },
  {
    title: "Stripe for franchise fees",
    what: "The Trades' own Stripe. Franchises pay the monthly fee and add-ons here.",
    keys: ["fee_stripe", "fee_stripe_pk"],
  },
  {
    title: "Stripe",
    what: "Card payments. Each company uses its own account, so money goes to that business.",
    keys: ["stripe", "stripe_publishable", "stripe_webhook"],
  },
];

const isKeyProvider = (p: string): p is KeyProvider => (KEY_PROVIDERS as readonly string[]).includes(p);

/** The cards a company at this level sees: only the keys it saves, and no empty cards. */
function keyGroupsAt(level: CompanyLevel) {
  const mine: readonly string[] = keyProvidersAt(level);
  return KEY_GROUPS.map((g) => ({ ...g, keys: g.keys.filter((k) => mine.includes(k)) })).filter((g) => g.keys.length > 0);
}

/** "Google sign-in client secret", "Meta app ID": the card name and the row name together, for the key window. */
function keyTitle(p: string): string {
  const group = KEY_GROUPS.find((g) => g.keys.includes(p))?.title;
  const name = PROVIDER_WORDS[p]?.[0] ?? p;
  return group ? `${group} ${name.charAt(0).toLowerCase()}${name.slice(1)}` : name;
}

// Where each key is made or changed, so the arrow beside a service opens that page (2026-10-29)
const KEY_PAGES: Record<string, string> = {
  stripe: "https://dashboard.stripe.com/apikeys",
  stripe_webhook: "https://dashboard.stripe.com/webhooks",
  stripe_publishable: "https://dashboard.stripe.com/apikeys",
  quo: "https://my.quo.com/settings/api",
  email: "https://resend.com/api-keys",
  google: "https://console.cloud.google.com/apis/credentials",
  google_maps: "https://console.cloud.google.com/google/maps-apis/credentials",
  anthropic: "https://platform.claude.com/settings/keys",
  plaid_client_id: "https://dashboard.plaid.com/developers/keys",
  plaid_secret: "https://dashboard.plaid.com/developers/keys",
  fee_stripe: "https://dashboard.stripe.com/apikeys",
  fee_stripe_pk: "https://dashboard.stripe.com/apikeys",
  meta: "https://business.facebook.com/settings/system-users",
  google_service: "https://console.cloud.google.com/iam-admin/serviceaccounts",
  google_ads_token: "https://console.cloud.google.com/apis/api/googleads.googleapis.com",
  google_client_id: "https://console.cloud.google.com/apis/credentials",
  google_client_secret: "https://console.cloud.google.com/apis/credentials",
  meta_app_id: "https://developers.facebook.com/apps",
  meta_app_secret: "https://developers.facebook.com/apps",
};

export function KeysSettings() {
  const { company } = useSession();
  const keys = useKeys(company.id);
  const save = useSaveKey();
  const remove = useRemoveKey();
  const toast = useToast();
  const [adding, setAdding] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mistake = adding && isKeyProvider(adding) ? keyMistake(adding, secret) : null;
  return (
    <>
      <Banner>Keys are stored locked (encrypted). After you save one, only its first 4 characters ever show again.</Banner>
      {keyGroupsAt(company.level).map((g, i) => (
        <Card key={g.title} id={i === 0 ? "keys" : `keys-${g.keys[0]}`} title={g.title} flush>
          <p className="muted small card-note">{g.what}</p>
          {g.keys.map((p) => {
            const k = keys.data?.find((x) => x.provider === p);
            const [name, what] = PROVIDER_WORDS[p] ?? [p, ""];
            const page = KEY_PAGES[p];
            return (
              <SettingRow
                key={p}
                title={
                  <>
                    {name}{" "}
                    {k ? (
                      <StatusLabel status="success">Saved · {k.preview}</StatusLabel>
                    ) : (
                      <StatusLabel status="inactive">Not set</StatusLabel>
                    )}
                  </>
                }
                description={
                  <>
                    {what}
                    {page ? (
                      <a
                        className="key-page"
                        href={page}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${keyTitle(p)} where the key lives`}
                        aria-label={`Open ${keyTitle(p)} where the key lives`}
                      >
                        <Icon name="arrowUpRight" size={14} />
                      </a>
                    ) : null}
                  </>
                }
                control={
                  <div className="row" style={{ flexWrap: "nowrap" }}>
                    {k ? (
                      <Button
                        type="tertiary"
                        variation="destructive"
                        size="small"
                        onBlur={() => setConfirming(null)}
                        onClick={() => {
                          if (confirming !== p) {
                            setConfirming(p);
                            return;
                          }
                          setConfirming(null);
                          remove.mutate(p, { onSuccess: () => toast(`${keyTitle(p)} removed`) });
                        }}
                      >
                        {confirming === p ? "Remove?" : "Remove"}
                      </Button>
                    ) : null}
                    <Button
                      type="secondary"
                      size="small"
                      onClick={() => {
                        setAdding(p);
                        setSecret("");
                        setError(null);
                      }}
                    >
                      {k ? "Replace" : "Add key"}
                    </Button>
                  </div>
                }
              />
            );
          })}
        </Card>
      ))}
      <p className="muted small">
        {company.level === "hq"
          ? "These run for every company. Each brand and location saves its own Stripe."
          : "Head office runs texts, emails, maps and AI for every company, so those keys are set there. Ask head office if one needs to change."}
      </p>
      <Modal
        title={adding ? keyTitle(adding) : ""}
        open={adding !== null}
        onClose={() => setAdding(null)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setAdding(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={secret.trim().length < 8 || !!mistake}
              onClick={() =>
                adding &&
                save.mutate(
                  { provider: adding, secret },
                  {
                    onSuccess: () => {
                      toast("Key saved");
                      setAdding(null);
                    },
                    onError: (e) => setError(errorText(e)),
                  },
                )
              }
            >
              Save key
            </Button>
          </>
        }
      >
        {error ? <Banner type="error">{error}</Banner> : null}
        {mistake ? <Banner type="warning">{mistake}</Banner> : null}
        {adding === "stripe" ? (
          <Banner type="warning">
            A test key (sk_test) works now. A live key (sk_live) is used only once the owner turns on real texts, emails and card
            charges for this brand.
          </Banner>
        ) : null}
        <InputText
          label="Secret key"
          type="text"
          className="secret"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          data-form-type="other"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          description="Paste it from the service's settings page."
        />
      </Modal>
    </>
  );
}
